import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import defaults from "@/config/siteContent.json";
import { trackingConfig } from "@/config/trackingConfig";
import { isMetaAdVisitor } from "@/lib/tracking/attribution";
import { http } from "@/lib/api";
import { setProductContext, setTrackingLanguage, trackLanguageChange } from "@/lib/tracking/tracker";

const SiteContext = createContext(null);

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const mergeContent = (remote) => ({ ...defaults, ...(remote && typeof remote === "object" ? remote : {}) });

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
// Translation overrides: objects merge by key, arrays of objects merge by index, everything else replaces.
const deepMerge = (base, over) => {
  if (over === undefined || over === null || over === "") return base;
  if (Array.isArray(base) && Array.isArray(over)) {
    if (!base.every(isObj)) return over;
    return base.map((item, i) => deepMerge(item, over[i]));
  }
  if (isObj(base) && isObj(over)) {
    const out = { ...base };
    Object.keys(over).forEach((k) => { out[k] = deepMerge(base[k], over[k]); });
    return out;
  }
  return over;
};

const LANGS = ["en", "hi"];
const initialLang = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (LANGS.includes(fromUrl)) return fromUrl;
  try { const saved = localStorage.getItem("tn_lang"); if (LANGS.includes(saved)) return saved; } catch { /* ignore */ }
  return "en";
};

// Dynamic number insertion: Meta ad visitors see AD_PHONE_NUMBER (if set); a call-tracking script can also set window.__TN_DYNAMIC_PHONE__.
const resolveContacts = (brand) => {
  const ad = trackingConfig.AD_PHONE_NUMBER && isMetaAdVisitor();
  return {
    phone: (typeof window !== "undefined" && window.__TN_DYNAMIC_PHONE__) || (ad && trackingConfig.AD_PHONE_NUMBER) || brand?.phone || trackingConfig.PHONE_NUMBER,
    phoneType: ad ? "meta_ad" : "default",
    whatsapp: (brand?.whatsapp || trackingConfig.WHATSAPP_NUMBER || "").replace(/\D/g, ""),
  };
};

export function SiteProvider({ children }) {
  const [raw, setContent] = useState(defaults);
  const [lang, setLangState] = useState(initialLang);
  const [modal, setModal] = useState({ type: null, cta: "", position: "" });
  const content = useMemo(() => {
    const base = { ...raw, ui: { ...defaults.ui, ...(raw.ui || {}) } };
    return lang === "en" ? base : deepMerge(base, deepMerge(defaults.translations?.[lang] || {}, raw.translations?.[lang]));
  }, [raw, lang]);

  useEffect(() => {
    document.documentElement.lang = lang === "hi" ? "hi" : "en-IN";
    setTrackingLanguage(lang);
    try { localStorage.setItem("tn_lang", lang); } catch { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next === lang) return;
    trackLanguageChange(lang, next);
    setLangState(next);
  }, [lang]);

  const t = useCallback((key, vars = {}) => {
    const s = content.ui?.[key] ?? defaults.ui?.[key] ?? key;
    return Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);
  }, [content]);

  useEffect(() => {
    let alive = true;
    http.get("/content").then((r) => { if (alive) setContent(mergeContent(r.data)); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setProductContext(content.product);
    const root = document.documentElement.style;
    [["primary", "forest"], ["accent", "gold"], ["leaf", "leaf"]].forEach(([key, name]) => {
      const rgb = hexToRgb(content.colors?.[key]);
      if (!rgb) return;
      root.setProperty(`--${name}`, content.colors[key]);
      root.setProperty(`--${name}-rgb`, rgb);
    });
  }, [content]);

  const openModal = useCallback((type, cta, position) => setModal({ type, cta, position }), []);
  const closeModal = useCallback(() => setModal((m) => ({ ...m, type: null })), []);

  const value = useMemo(() => ({
    content, contacts: resolveContacts(content.brand), modal, openModal, closeModal, lang, setLang, t,
  }), [content, modal, openModal, closeModal, lang, setLang, t]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
